import PropTypes from 'prop-types';
import { useEffect, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { useForm, useWatch } from 'react-hook-form';
import { Row, Col, Form } from 'react-bootstrap';

import { usePrevious } from '_hooks';
import { theme } from '_constants/theme';
import { PRODUCT_CODES } from '_constants/products';
import {
  EXCLUDE_ANNUAL_COMPLIANCE_REPORT_STATES,
  FIELDS_KEYS,
} from '_constants/forms';

import { getFieldProps } from '_helpers/forms/fields';

import Card from '_components/atomic/layouts/Card';
import BadgeSelectField from '_components/atomic/fields/BadgeSelectField';
import PrimaryButton from '_components/atomic/PrimaryButton';

const StyledForm = styled(Form)`
  @media ${theme.devices.tablet} {
    flex-direction: column;
    padding: ${theme.spaces.regular}px;
  }
`;

const StateAndEntitySelectForm = ({
  submitRef,
  error,
  geolocation,
  prices = {},
  submitDisabled,
  defaultValues,
  onSubmit,
}) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    setValue,
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const values = useWatch({ control });
  const prevValues = usePrevious(values);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (
      !values[FIELDS_KEYS.organizedState] &&
      geolocation?.region &&
      geolocation?.countryCode === 'US'
    ) {
      setValue(FIELDS_KEYS.organizedState, geolocation.region);
    }
  }, [values, geolocation, setValue]);

  useEffect(() => {
    if (
      prevValues &&
      prevValues[FIELDS_KEYS.organizedState] !==
        values[FIELDS_KEYS.organizedState]
    ) {
      if (
        EXCLUDE_ANNUAL_COMPLIANCE_REPORT_STATES.includes(
          values[FIELDS_KEYS.organizedState]
        )
      ) {
        setValue(FIELDS_KEYS.compliance, false);
      } else {
        setValue(FIELDS_KEYS.compliance, true);
      }
    }
  }, [prevValues, values, setValue]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [reset, defaultValues]);

  useImperativeHandle(submitRef, () => ({
    submit: handleSubmit(onSubmit),
  }));

  return (
    <StyledForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <Card.Text
        asTitle
        size={theme.fontSizes.small}
        color={theme.colors.indigo[600]}
        bottomSpace={theme.spaces.small}
        desktop
      >
        FREE LLC FILING + STATE FEE
      </Card.Text>
      <Card.Text
        asTitle
        size={theme.fontSizes.smallest + 2}
        color={theme.colors.indigo[600]}
        bottomSpace={theme.spaces.small}
        mobile
      >
        FREE LLC FILING + STATE FEE
      </Card.Text>
      <Card.Text
        size={theme.fontSizes.large13X}
        lineHeightRatio={1.1}
        weight={700}
        bottomSpace={theme.spaces.largeX}
        desktop
      >
        Register Your LLC <br /> in Minutes
      </Card.Text>
      <Card.Text
        size={theme.fontSizes.large4X}
        lineHeightRatio={1.1}
        weight={700}
        bottomSpace={theme.spaces.largeX}
        mobile
      >
        Register Your LLC <br /> in Minutes
      </Card.Text>
      <Card.Text
        size={theme.fontSizes.largeX}
        weight={700}
        bottomSpace={theme.spaces.large4X}
        desktop
      >
        Price: ${prices[PRODUCT_CODES.incLLC]} + State Fee
      </Card.Text>
      <Card.Text
        size={theme.fontSizes.regular}
        weight={700}
        bottomSpace={theme.spaces.largeX}
        mobile
      >
        Price: ${prices[PRODUCT_CODES.incLLC]} + State Fee
      </Card.Text>
      <Card.Text
        size={theme.fontSizes.largeX}
        weight={300}
        bottomSpace={theme.spaces.large6X}
        desktop
      >
        Join Over 1,000,000 Businesses Launched With Incorpified Since 2004
      </Card.Text>
      <Card.Text
        size={theme.fontSizes.regular}
        weight={300}
        bottomSpace={theme.spaces.largeX}
        mobile
      >
        Join Over 1,000,000 Businesses Launched With Incorpified Since 2004
      </Card.Text>
      <Row>
        <Col xs={12} md={9}>
          <BadgeSelectField
            {...getFieldProps(FIELDS_KEYS.entity)}
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>
        <Col xs={12} md={9}>
          <BadgeSelectField
            {...getFieldProps(FIELDS_KEYS.organizedState)}
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>
        <Col xs={2} md={12} />
        <Col xs={8} md={9}>
          <PrimaryButton type="submit" disabled={submitDisabled}>
            GET STARTED
          </PrimaryButton>
        </Col>
      </Row>

      {error && (
        <Card.Text weight={700} color={theme.colors.orange[600]}>
          {error?.message}
        </Card.Text>
      )}
    </StyledForm>
  );
};

StateAndEntitySelectForm.propTypes = {
  submitRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  error: PropTypes.object,
  geolocation: PropTypes.object,
  prices: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default StateAndEntitySelectForm;
